import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import AdminLayout from '../AdminLayout';
import {
  useGetCaseByIdQuery,
  useSendAdminMessageMutation
} from 'store/services/case/caseApi';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { AssetUrls } from 'config/assets';
import AdminActionsPanel from './components/AdminActionsPanel';
import CaseTimeline from './components/CaseTimeline';
import AdminEditTradeItemModal from '../Trades/components/AdminEditTradeItemModal';
import AdminRemoveCardConfirmModal from '../Trades/components/AdminRemoveCardConfirmModal';
import {
  AdminTrade,
  AdminTradeItem,
  useAdminCompleteCardInTradeMutation,
} from 'store/services/trade/tradeApi';
import { Points } from 'components/Points/Points';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { EmojiPicker } from 'components/EmojiPicker/EmojiPicker';
import { ChatImageLightbox } from 'components/ChatImageLightbox/ChatImageLightbox';
import { MessageImage } from 'types/interfaces/message';
import WhySummonedPanel from './components/WhySummonedPanel';
import { formatCaseNumber } from 'utils/caseNumber';
import { eventLabel } from 'screens/Main/tabs/Inbox/threadSubject';
import { TRADEMAGIC_AVATAR_URL } from 'utils/messageHelpers';
import { formatConditionShort } from 'utils/offerErrors';
import { FinishMark } from 'components/FinishTag/FinishTag';
import { fromHaveFinish } from 'utils/finishes';
import { formatLanguage } from 'constants/languages';
import 'styles/caseStatusBadges.css';
import 'styles/adminMessage.css';
import './CaseDetailPage.css';

export default function CaseDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const [adminMessage, setAdminMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AdminTradeItem | null>(null);
  const [removingItem, setRemovingItem] = useState<AdminTradeItem | null>(null);
  const [completingItemId, setCompletingItemId] = useState<string | null>(null);
  const [lightboxImages, setLightboxImages] = useState<MessageImage[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showEarlier, setShowEarlier] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasLoadedMessagesRef = useRef(false);
  const prevMessageCountRef = useRef(0);

  const { data, isLoading, error, refetch } = useGetCaseByIdQuery(caseId!);
  const [sendAdminMessage, { isLoading: isSending }] = useSendAdminMessageMutation();
  const [completeCard] = useAdminCompleteCardInTradeMutation();

  // Auto-scroll the conversation to the latest message ONLY when new messages
  // arrive during the session (e.g. after the admin sends one). On the initial
  // load we deliberately do not scroll, so the page opens at the top with the
  // Trade Details section visible. Scrolling is scoped to the messages
  // container so it never moves the outer page.
  useEffect(() => {
    const count = data?.messages?.length ?? 0;
    if (!hasLoadedMessagesRef.current) {
      hasLoadedMessagesRef.current = true;
      prevMessageCountRef.current = count;
      return;
    }
    if (count > prevMessageCountRef.current) {
      const container = messagesEndRef.current?.parentElement;
      if (container) container.scrollTop = container.scrollHeight;
    }
    prevMessageCountRef.current = count;
  }, [data?.messages]);

  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = adminMessage.substring(0, start) + emoji + adminMessage.substring(end);

    setAdminMessage(newMessage);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  const handleSendMessage = async () => {
    if (!adminMessage.trim() || !caseId) return;

    try {
      await sendAdminMessage({
        caseId,
        content: adminMessage.trim()
      }).unwrap();

      setAdminMessage('');
      toast.success('Message sent to both parties');
    } catch (error: any) {
      console.error('Failed to send admin message:', error);
      toast.error(error.data?.message || 'Failed to send message');
    }
  };

  const openLightbox = (images: MessageImage[], index: number) => {
    setLightboxImages(images);
    setLightboxIndex(index);
  };

  const formatMessageTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <AdminLayout title="Case Detail" currentPath="/admin/cases">
        <div className="case-detail-page">
          <div className="case-detail-loading">Loading case details...</div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !data) {
    return (
      <AdminLayout title="Case Detail" currentPath="/admin/cases">
        <div className="case-detail-page">
          <div className="case-detail-error">
            {(error as any)?.status === 404
              ? "This case doesn't exist or you don't have access."
              : 'Failed to load case details. Please try again.'}
          </div>
          <button onClick={() => navigate('/admin/cases')} className="back-button">
            Back to Cases
          </button>
        </div>
      </AdminLayout>
    );
  }

  const { case: caseDetails, messages, dividerTimestamp } = data;
  const trade = caseDetails.trade;

  // Adapt a case-detail trade item to the AdminTradeItem shape consumed by the
  // shared admin edit/remove modals.
  const adaptItem = (item: typeof trade.items[number]): AdminTradeItem => ({
    id: item.id!,
    name: item.name,
    set: item.set || item.setName || '',
    setCode: item.setCode || '',
    scryfallId: item.scryfallID || '',
    quantity: item.quantity,
    condition: item.condition,
    language: item.language || 'en',
    foil: item.foil ?? false,
    // Authoritative finish so an ETCHED card does not preselect FOIL in the modal.
    finish: item.finish ?? null,
    pointValue: item.pointValue,
  });

  // Per-card complete: one-click action that credits the sender for a single card
  // (the economic mirror of Remove, which refunds the receiver). The server enforces
  // mutual exclusivity (a settled card can't be re-settled), so no confirm modal is needed.
  const handleCompleteItem = async (item: typeof trade.items[number]) => {
    if (!item.id) return;
    setCompletingItemId(item.id);
    try {
      const res = await completeCard({ tradeId: trade.id, itemId: item.id }).unwrap();
      const credited = res.creditedPoints ?? item.pointValue;
      toast.success(`${item.name} completed. ${credited} points credited to sender.`);
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to complete card. Please try again.');
    } finally {
      setCompletingItemId(null);
    }
  };

  // AdminTrade shape shared by AdminEditTradeItemModal and AdminRemoveCardConfirmModal.
  const adminTrade: AdminTrade = {
    id: trade.id,
    tradeNumber: trade.tradeNumber,
    status: trade.status as AdminTrade['status'],
    senderId: trade.sender.id,
    senderUsername: trade.sender.username,
    senderAvatar: trade.sender.avatarUrl,
    receiverId: trade.receiver.id,
    receiverUsername: trade.receiver.username,
    receiverAvatar: trade.receiver.avatarUrl,
    totalValue: trade.totalValue,
    initiatedAt: trade.createdAt,
    items: [],
  };

  const affectedItemIds = new Set(caseDetails.affectedItemIds ?? []);
  // Change 2: the thread is the trade thread for every new-path case, so the
  // pre-divider messages collapse for all of them; the heading differs for
  // legacy cases, whose earlier rows are the admin-party context.
  // legacy cases keep the Context block as it was (Decision 22).
  const isWizardCase = caseDetails.reason != null;

  // Split messages by divider timestamp
  const messagesBeforeDivider = messages.filter(
    m => new Date(m.createdAt) < new Date(dividerTimestamp)
  );
  const messagesAfterDivider = messages.filter(
    m => new Date(m.createdAt) >= new Date(dividerTimestamp)
  );

  return (
    <AdminLayout title="Case Detail" currentPath="/admin/cases">
      <div className="case-detail-page">
        {/* Header */}
        <div className="case-detail-header">
          <button onClick={() => navigate('/admin/cases')} className="back-button">
            ← Back to Cases
          </button>
          <div className="case-detail-header-info">
            <h1 className="case-detail-title">
              {formatCaseNumber(caseDetails.caseNumber)
                ? `Case ${formatCaseNumber(caseDetails.caseNumber)} · Trade ${trade.tradeNumber}`
                : `Case: ${trade.tradeNumber}`}
            </h1>
            <span className={`case-status-badge status-${caseDetails.status.toLowerCase()}`}>
              {caseDetails.status.replace(/_/g, ' ')}
            </span>
            {caseDetails.reopenedAt && (
              <span className="case-status-badge status-reopened" data-testid="case-reopened-chip">
                Reopened {formatDate(caseDetails.reopenedAt)}
              </span>
            )}
          </div>
        </div>

        {/* Why you were summoned (Asana 1219952262403623, Decision 22) */}
        <WhySummonedPanel caseDetails={caseDetails} onOpenImage={openLightbox} />

        {/* Trade Details Panel */}
        <div className="trade-details-panel">
          <h2 className="panel-title">Trade Details</h2>

          <div className="trade-parties">
            <div className="trade-party">
              <span className="party-label">Sender:</span>
              <div className="party-info">
                <AvatarWithFlag
                  avatarUrl={trade.sender.avatarUrl}
                  username={trade.sender.username}
                  size={48}
                  countryCode={trade.sender.country}
                  isElite={trade.sender.isElite}
                  showAnimatedBorder={trade.sender.showAnimatedBorder}
                  borderColor1={trade.sender.borderColor1}
                  borderColor2={trade.sender.borderColor2}
                />
                <div>
                  <div className="party-username">{trade.sender.username}</div>
                  <div className="party-email">{trade.sender.email}</div>
                </div>
              </div>
            </div>

            <div className="trade-arrow">→</div>

            <div className="trade-party">
              <span className="party-label">Receiver:</span>
              <div className="party-info">
                <AvatarWithFlag
                  avatarUrl={trade.receiver.avatarUrl}
                  username={trade.receiver.username}
                  size={48}
                  countryCode={trade.receiver.country}
                  isElite={trade.receiver.isElite}
                  showAnimatedBorder={trade.receiver.showAnimatedBorder}
                  borderColor1={trade.receiver.borderColor1}
                  borderColor2={trade.receiver.borderColor2}
                />
                <div>
                  <div className="party-username">{trade.receiver.username}</div>
                  <div className="party-email">{trade.receiver.email}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="trade-meta">
            <div className="trade-meta-item">
              <span className="meta-label">Trade Status:</span>
              <span className="meta-value">{trade.status}</span>
            </div>
            <div className="trade-meta-item">
              <span className="meta-label">Trade Date:</span>
              <span className="meta-value">{formatDate(trade.createdAt)}</span>
            </div>
            {trade.shippedAt && (
              <div className="trade-meta-item">
                <span className="meta-label">Shipped:</span>
                <span className="meta-value">{formatDate(trade.shippedAt)}</span>
              </div>
            )}
            <div className="trade-meta-item">
              <span className="meta-label">Total Value:</span>
              <Points points={trade.totalValue} type="small" />
            </div>
            <div className="trade-meta-item">
              <span className="meta-label">Case Date:</span>
              <span className="meta-value">{formatDate(caseDetails.createdAt)}</span>
            </div>
          </div>

          <div className="trade-items">
            <h3 className="items-title">Items in Trade ({trade.items.length})</h3>
            <div className="items-list">
              <div className="trade-item trade-item--header" aria-hidden="true">
                <span>Qty</span>
                <span>Card</span>
                <span>Set</span>
                <span>Finish</span>
                <span>Condition</span>
                <span>Lang</span>
                <span>Value</span>
                <span />
              </div>
              {trade.items.map((item, idx) => {
                const reported = !!item.id && affectedItemIds.has(item.id);
                const settled = !!item.completedAt || !!item.removedAt;
                const finish = item.finish ? fromHaveFinish(item.finish) : item.foil ? 'foil' : 'nonfoil';
                return (
                  <div
                    key={item.id ?? `row-${idx}`}
                    className={`trade-item${reported ? ' trade-item--reported' : ''}${settled ? ' trade-item--settled' : ''}`}
                    data-testid="trade-item-row"
                  >
                    <span className="item-qty">{item.quantity}x</span>
                    <span className="item-name">
                      {item.name}
                      {reported && <span className="item-reported">Reported</span>}
                    </span>
                    <span className="item-set">{item.set || item.setCode}</span>
                    <span className="item-finish">
                      {finish === 'nonfoil' ? <span className="item-finish-none">Non-foil</span> : <FinishMark finish={finish} />}
                    </span>
                    <span className="item-condition">{formatConditionShort(item.condition)}</span>
                    <span className="item-language">{formatLanguage(item.language || 'en')}</span>
                    <Points points={item.pointValue * item.quantity} type="small" />
                    {item.removedAt ? (
                      <span className="item-settled-badge item-settled-badge--cancelled">Cancelled · receiver refunded</span>
                    ) : item.completedAt ? (
                      <span className="item-settled-badge item-settled-badge--completed">Completed · sender credited</span>
                    ) : item.id ? (
                      <div className="item-actions">
                        <button
                          className="icon-btn"
                          onClick={() => {
                            setEditingItem(adaptItem(item));
                            setEditModalOpen(true);
                          }}
                          title="Edit card (set, finish, language, condition)"
                          aria-label={`Edit ${item.name}`}
                        >
                          <img
                            src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/edit.svg"
                            alt=""
                            width="14"
                            height="14"
                            style={{ filter: 'brightness(0) invert(1)' }}
                          />
                        </button>
                        <button
                          className="icon-btn"
                          onClick={() => setRemovingItem(adaptItem(item))}
                          title="Cancel card (refunds the receiver)"
                          aria-label={`Cancel ${item.name}`}
                        >
                          <svg width="18" height="18" viewBox="0 0 14 14" fill="currentColor">
                            <path d="M3.293 2.586 2.586 3.293 5.293 6l-2.707 2.707.707.707L6 6.707l2.707 2.707.707-.707L6.707 6l2.707-2.707-.707-.707L6 5.293 3.293 2.586z" transform="translate(1 1)" />
                          </svg>
                        </button>
                        <button
                          className="icon-btn icon-btn-complete"
                          onClick={() => handleCompleteItem(item)}
                          disabled={completingItemId === item.id}
                          title="Complete card (credits the sender)"
                          aria-label={`Complete ${item.name}`}
                        >
                          <svg width="18" height="18" viewBox="0 0 14 14" fill="currentColor">
                            <path d="M11.7 3.3 5.5 9.5 2.3 6.3l-1 1 4.2 4.2 7.2-7.2z" />
                          </svg>
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Conversation Thread */}
        <div className="conversation-panel">
          <h2 className="panel-title">Conversation</h2>

          <div className="messages-container">
            {/* Messages before admin summoned */}
            {messagesBeforeDivider.length > 0 && !showEarlier && (
              <button type="button" className="earlier-messages-toggle" onClick={() => setShowEarlier(true)}>
                ▸ {messagesBeforeDivider.length} earlier message{messagesBeforeDivider.length === 1 ? '' : 's'}
              </button>
            )}
            {messagesBeforeDivider.length > 0 && showEarlier && (
              <div className="messages-section">
                <div className="section-label">
                  {isWizardCase ? 'Earlier messages from the trade thread' : 'Context (before admin summoned)'}
                </div>
                {messagesBeforeDivider.map((message) => (
                  <div
                    key={message.id}
                    className={`message-bubble ${
                      message.sender.isAdminMessage ? 'admin-message' : 'user-message'
                    }`}
                  >
                    <div className="message-sender-info">
                      <AvatarWithFlag
                        avatarUrl={message.sender.avatarUrl || (message.senderRole === 'SYSTEM' ? TRADEMAGIC_AVATAR_URL : undefined)}
                        username={message.sender.username}
                        size={28}
                      />
                      <span className="message-sender">{message.sender.username}</span>
                      <span className="message-time">{formatMessageTime(message.createdAt)}</span>
                    </div>
                    <div className="message-content">{message.content}</div>
                    {message.images && message.images.length > 0 && (
                      <div className="message-images">
                        {message.images.map((img, idx) => (
                          <img
                            key={img.id}
                            src={img.url}
                            alt={`Attachment ${idx + 1}`}
                            className="message-image-thumbnail"
                            loading="lazy"
                            onClick={() => openLightbox(message.images!, idx)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Divider */}
            <div className="admin-summon-divider">
              <div className="divider-line"></div>
              <div className="divider-text">
                ADMIN SUMMONED ON {formatDate(dividerTimestamp)}
              </div>
              <div className="divider-line"></div>
            </div>

            {/* Messages after admin summoned */}
            <div className="messages-section">
              {messagesAfterDivider.length === 0 ? (
                <div className="empty-messages">
                  No messages yet. The conversation will appear here once the parties communicate.
                </div>
              ) : (
                messagesAfterDivider.map((message) => message.kind && message.kind !== 'ADMIN_SUMMONED' ? (
                  // Case phase rows (change 2): resolved, reopened, admin joined.
                  <div key={message.id} className="case-event-row" role="note">
                    <span className="case-event-label">{eventLabel(message.kind) ?? message.kind}</span>
                    <span className="case-event-time">{formatMessageTime(message.createdAt)}</span>
                    {message.content.trim() && <span className="case-event-detail">{message.content}</span>}
                  </div>
                ) : (
                  <div
                    key={message.id}
                    className={`message-bubble ${
                      message.sender.isAdminMessage ? 'admin-message' : 'user-message'
                    }`}
                  >
                    <div className="message-sender-info">
                      {message.sender.isAdminMessage ? (
                        <img
                          src={AssetUrls.logos.iconLogo}
                          alt="TradeMagic Admin"
                          className="message-avatar"
                        />
                      ) : (
                        <AvatarWithFlag
                          avatarUrl={message.sender.avatarUrl || (message.senderRole === 'SYSTEM' ? TRADEMAGIC_AVATAR_URL : undefined)}
                          username={message.sender.username}
                          size={28}
                        />
                      )}
                      <span className="message-sender">
                        {message.sender.isAdminMessage ? 'TradeMagic Admin' : message.sender.username}
                      </span>
                      <span className="message-time">{formatMessageTime(message.createdAt)}</span>
                    </div>
                    <div className="message-content">{message.content}</div>
                    {message.images && message.images.length > 0 && (
                      <div className="message-images">
                        {message.images.map((img, idx) => (
                          <img
                            key={img.id}
                            src={img.url}
                            alt={`Attachment ${idx + 1}`}
                            className="message-image-thumbnail"
                            loading="lazy"
                            onClick={() => openLightbox(message.images!, idx)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            <div ref={messagesEndRef} />
          </div>

          {/* Admin Message Input */}
          <div className="admin-message-section">
            <h3 className="message-section-title">Send Message to Both Parties</h3>
            <div className="message-input-group">
              <textarea
                ref={textareaRef}
                className="message-textarea"
                placeholder="Type your message here..."
                value={adminMessage}
                onChange={(e) => setAdminMessage(e.target.value)}
                rows={4}
                maxLength={2000}
              />
              <CharacterCounter
                currentLength={adminMessage.length}
                maxLength={2000}
                warningThreshold={1800}
              />
            </div>

            <div className="message-actions">
              <div className="emoji-picker-container">
                <button
                  ref={emojiButtonRef}
                  className="emoji-picker-button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  type="button"
                >
                  😀
                </button>
                {showEmojiPicker && (
                  <EmojiPicker
                    onEmojiSelect={handleEmojiSelect}
                    onClose={() => setShowEmojiPicker(false)}
                    buttonRef={emojiButtonRef}
                  />
                )}
              </div>

              <button
                onClick={handleSendMessage}
                disabled={!adminMessage.trim() || isSending}
                className="send-message-button"
              >
                {isSending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>

        {/* Admin Actions Panel */}
        <AdminActionsPanel
          caseId={caseId!}
          caseStatus={caseDetails.status}
          tradeStatus={trade?.status}
          allowedActions={caseDetails.allowedActions}
          canReopen={'reopenedAt' in caseDetails}
        />

        {/* Case history: every recorded admin action, oldest first */}
        <CaseTimeline actions={data.adminActions ?? []} />

        {/* Edit Trade Item Modal */}
        {editModalOpen && editingItem && (
          <AdminEditTradeItemModal
            trade={adminTrade}
            item={editingItem}
            onClose={() => {
              setEditModalOpen(false);
              setEditingItem(null);
              refetch();
            }}
          />
        )}

        {/* Remove Trade Item Modal */}
        {removingItem && (
          <AdminRemoveCardConfirmModal
            trade={adminTrade}
            item={removingItem}
            onClose={() => {
              setRemovingItem(null);
              refetch();
            }}
          />
        )}

        {/* Image Lightbox */}
        {lightboxImages && (
          <ChatImageLightbox
            images={lightboxImages}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxImages(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}
